import React from 'react';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import { Box, Flex, InlineLink, Modal, Pane, SelectMenu, Text } from '@fullstory/components';
import { SegmentSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import {
  getCustomSegmentKey,
  getCustomSegmentLabel,
  getDefaultSegmentScope,
  segmentsSelectors,
} from '@fullstory/redux';
import { Scope } from '@fullstory/proto/fs/api/search';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { intl } from '@fullstory/intl';
import { FormattedMessage } from 'react-intl';

const { getSegmentsById } = segmentsSelectors;
const options = [
  {
    label: intl.formatMessage({ defaultMessage: 'By the matching users', id: 'vTQqwt' }),
    key: 'indiv',
    value: Scope.SCOPE_INDIVIDUAL,
  },
  {
    label: intl.formatMessage({ defaultMessage: 'Within the matching sessions', id: 'UBFm6n' }),
    key: 'session',
    value: Scope.SCOPE_SESSION,
  },
];

export const SegmentScopingModal: React.FC<
  Pick<React.ComponentProps<typeof Modal>, 'onCloseSignal'> & {
    segmentSettings: SegmentSettings[];
    onChange: (segmentSettings: SegmentSettings[]) => void;
    segmentScopeOptionText: string;
    matchingUsersText: string;
    withinMatchingSessionsText: string;
    open?: boolean;
  }
> = ({
  segmentSettings,
  onCloseSignal,
  onChange,
  segmentScopeOptionText,
  matchingUsersText,
  withinMatchingSessionsText,
  open,
}) => {
  const byId = useSelector(getSegmentsById);
  const customSegments = segmentSettings.filter(settings => !settings.segmentId);
  return (
    <Modal data-component="SegmentScopingModal" onCloseSignal={onCloseSignal} open={open}>
      <Formik initialValues={[...segmentSettings]} onSubmit={values => onChange(values)}>
        {({ values, submitForm, setValues }) => (
          <>
            <Modal.Header onCloseSignal={onCloseSignal}>
              <FormattedMessage defaultMessage="Advanced Options" id="KxN+uh" />
            </Modal.Header>
            <Modal.Body>
              <Box marginBottom={16}>
                <Text appearance="bodyMedium">
                  {segmentScopeOptionText}{' '}
                  <InlineLink
                    href="https://help.fullstory.com/hc/en-us/articles/4411050765207"
                    newTab
                  >
                    <FormattedMessage defaultMessage="Learn more." id="hl3R0S" />
                  </InlineLink>
                </Text>
              </Box>
              <Pane backgroundColor="secondary" marginBottom={20} padding={16} borderRadius={6}>
                <Box marginBottom={4}>
                  <Text appearance="bodySmallBold">
                    <FormattedMessage defaultMessage="By the matching users" id="vTQqwt" />
                  </Text>
                </Box>
                <Box marginBottom={20}>
                  <Text appearance="bodySmall">{matchingUsersText}</Text>
                </Box>
                <Box marginBottom={4}>
                  <Text appearance="bodySmallBold">
                    <FormattedMessage defaultMessage="Within the matching sessions" id="UBFm6n" />
                  </Text>
                </Box>
                <Box>
                  <Text appearance="bodySmall">{withinMatchingSessionsText}</Text>
                </Box>
              </Pane>
              <Box marginBottom={12}>
                <Flex>
                  <Box width={0.5}>
                    <Text appearance="bodyLargeBold">
                      <FormattedMessage defaultMessage="Segment" id="OrwKQ+" />
                    </Text>
                  </Box>
                  <Box width={0.5}>
                    <Text appearance="bodyLargeBold">
                      <FormattedMessage defaultMessage="Counting events performed" id="SVzlBw" />
                    </Text>
                  </Box>
                </Flex>
              </Box>
              {values.map((settings, i) => {
                let segmentDefinition: SegmentDefinition | null;
                let segmentId: string;
                let segmentName: string;
                if (settings.segmentId) {
                  segmentDefinition =
                    byId?.[settings.segmentId]?.segment?.segment?.segmentDefinition ?? null;
                  segmentId = settings.segmentId;
                  segmentName = byId?.[settings.segmentId]?.segment?.segment?.name ?? '';
                } else if (settings.segmentDefinition || settings.namedSegmentDefinition) {
                  const index = customSegments.indexOf(settings);
                  // we can assert that segmentDefinition exists or is null in one of these two
                  // because of the one-of constraint on SegmentSettings
                  segmentDefinition = (settings.segmentDefinition ||
                    settings.namedSegmentDefinition?.segmentDefinition)!;
                  segmentId = getCustomSegmentKey(index);
                  segmentName = getCustomSegmentLabel(index, settings);
                } else {
                  return null;
                }
                return (
                  <Box marginBottom={8} key={segmentId}>
                    <Flex>
                      <Box width={0.5}>
                        <Text appearance="bodySmall">{segmentName}</Text>
                      </Box>
                      <Box width={0.5}>
                        <SelectMenu
                          variant="outline"
                          size="x-small"
                          options={options}
                          selected={
                            options.find(
                              opt =>
                                opt.value ===
                                // segment scope from settings, if it exists
                                (settings.segmentScope ||
                                  // or the default segment scope from the segment
                                  (segmentDefinition && getDefaultSegmentScope(segmentDefinition))),
                              // or default to segment scoping
                            ) || options[1]
                          }
                          onChange={opt => {
                            if (!opt) return;
                            values[i] = {
                              ...values[i],
                              segmentScope: opt.value,
                            };
                            setValues(values);
                          }}
                        />
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
            </Modal.Body>
            <Modal.FooterActions
              primaryAction={{
                action: () => submitForm(),
                text: intl.formatMessage({
                  defaultMessage: 'Apply',
                  id: 'gIQxJX',
                  description: 'Apply changes and close popup',
                }),
              }}
              secondaryAction={{
                action: onCloseSignal,
                text: intl.formatMessage({
                  defaultMessage: 'Cancel',
                  id: 'JHJyH+',
                  description: 'Cancel and close popup',
                }),
              }}
            />
          </>
        )}
      </Formik>
    </Modal>
  );
};
